import React, {useEffect, useState, useMemo} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import EmptyData from "@/components/EmptyData";
import VLFSelectNew from "@/components/Input/SelectNew";

import {RosterAPI} from "@/api/roster";
import {PlayerAPI} from "@/api/player";
import {MatchAPI} from "@/api/match";
import {TrainingAPI} from "@/api/training";
import {fetchPlayerRating} from "@/utils/playerRating";

import {ERROR_OPERATION, dateFormat} from "@/utils/constants";

import "./index.scss";

function DirectorDashboardPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const season = useSelector((state) => state?.season);
	const [loading, setLoading] = useState(false);
	const [rosters, setRosters] = useState([]);
	const [kpiData, setKpiData] = useState({});
	const [selectedRoster, setSelectedRoster] = useState();

	// Recupera tutte le rose
	const getRosters = async () => {
		setLoading(true);
		try {
			const response = await RosterAPI.list({seasonId});
			// Filtra solo le rose interne (non prospect) e con categoria non basicActivities (da Primavera a Under14)
			const internalRosters = response.filter(
				(r) => !r.prospect && !r.playerCategory?.basicActivities,
			);
			setRosters(internalRosters);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getLatestDate = (items, field) => {
		if (!items || !items.length) {
			return null;
		}

		return items.reduce((latest, item) => {
			const dateValue = item?.[field];
			if (!dateValue) {
				return latest;
			}
			if (!latest) {
				return dateValue;
			}
			return moment(dateValue).isAfter(latest) ? dateValue : latest;
		}, null);
	};

	const parseTqrValue = (value) => {
		if (value === null || value === undefined) {
			return null;
		}
		if (typeof value === "string" && value.indexOf("-") !== -1) {
			value = value.split(" - ")[0];
		}
		const parsed = parseFloat(value);
		return Number.isNaN(parsed) ? null : parsed;
	};

	// Calcola KPI per una rosa
	const calculateRosterKPIs = async (roster) => {
		try {
			const seasonStartDate =
				season?.startDate || moment().startOf("year").format("YYYY-MM-DD");
			const seasonEndDate =
				season?.endDate || moment().endOf("year").format("YYYY-MM-DD");

			const [trainings, matches] = await Promise.all([
				TrainingAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					categoryId: roster?.playerCategory?.id,
					seasonId,
				}),
				MatchAPI.list({
					fromDate: seasonStartDate,
					toDate: seasonEndDate,
					matchObserved: false,
					categoryId: roster?.playerCategory?.id,
					seasonId,
				}),
			]);

			const trainingsCount = trainings?.length || 0;
			const matchesPlayed =
				matches?.filter((m) => moment(m.startDate).isSameOrBefore(moment()))
					?.length || 0;

			const rosterPlayers = await RosterAPI.getPlayers(roster.id);
			const players = rosterPlayers
				?.map((rp) => rp.player)
				?.filter((p) => p && p.status === "Interno" && p.goalkeeper === true);

			if (!players || !players.length) {
				return {
					rosterId: roster.id,
					players: [],
					topForm: [],
					lowForm: [],
					topRated: [],
					topRPE: [],
					lowTQR: [],
					trainingsCount,
					matchesPlayed,
					avgLoadWeek: null,
					avgTqrWeek: null,
				};
			}

			// Calcola TQR e RPE per ogni giocatore
			const fromDate = moment().subtract(7, "days").format("YYYY-MM-DD");
			const toDate = moment().format("YYYY-MM-DD");

			const playerStats = await Promise.all(
				players.map(async (player) => {
					try {
						// Recupera TQR (recupero)
						const tqrData = await PlayerAPI.playerListTQR(player.id, {
							fromDate,
							toDate,
						});
						const avgTQR =
							tqrData && tqrData.length
								? tqrData.reduce(
										(sum, t) => sum + parseFloat(t.tqrValue || 0),
										0,
									) / tqrData.length
								: null;
						const tqrLatestDate = getLatestDate(tqrData, "tqrDate");
						const tqrValues = (tqrData || [])
							.map((t) => parseTqrValue(t.tqrValue))
							.filter((v) => v !== null);
						const tqrSum = tqrValues.reduce((sum, v) => sum + v, 0);
						const tqrCount = tqrValues.length;

						// Recupera RPE (carico)
						const rpeData = await PlayerAPI.listRPE({
							playerId: player.id,
							fromDate,
							toDate,
						});
						const avgRPE =
							rpeData && rpeData.length
								? rpeData.reduce((sum, r) => sum + parseFloat(r.rpe || 0), 0) /
									rpeData.length
								: null;
						const rpeLatestDate = getLatestDate(rpeData, "rpeDate");
						const loadValues = (rpeData || [])
							.map((r) => parseFloat(r.trainingLoad))
							.filter((v) => !Number.isNaN(v));
						const rpeLoadSum = loadValues.reduce((sum, v) => sum + v, 0);
						const rpeLoadCount = loadValues.length;

						// Recupera rating
						const rating = await fetchPlayerRating(player.id, seasonId);

						return {
							player,
							avgTQR,
							avgRPE,
							rating,
							tqrCount: tqrData?.length || 0,
							rpeCount: rpeData?.length || 0,
							tqrLatestDate,
							rpeLatestDate,
							tqrSum,
							tqrCount,
							rpeLoadSum,
							rpeLoadCount,
						};
					} catch (error) {
						console.error(
							`Error fetching stats for player ${player.id}:`,
							error,
						);
						return {
							player,
							avgTQR: null,
							avgRPE: null,
							rating: null,
							tqrCount: 0,
							rpeCount: 0,
							tqrLatestDate: null,
							rpeLatestDate: null,
							tqrSum: 0,
							tqrCount: 0,
							rpeLoadSum: 0,
							rpeLoadCount: 0,
						};
					}
				}),
			);

			// Filtra giocatori con dati validi
			const playersWithTQR = playerStats.filter((ps) => ps.avgTQR !== null);
			const playersWithRPE = playerStats.filter((ps) => ps.avgRPE !== null);
			const playersWithRating = playerStats.filter((ps) => ps.rating !== null);

			// Top 3 in forma (TQR più alto)
			const topForm = [...playersWithTQR]
				.sort((a, b) => (b.avgTQR || 0) - (a.avgTQR || 0))
				.slice(0, 3)
				.map((ps) => ({
					...ps.player,
					value: ps.avgTQR,
					type: "TQR",
					date: ps.tqrLatestDate,
				}));

			// Top 3 meno in forma (TQR più basso)
			const lowForm = [...playersWithTQR]
				.sort((a, b) => (a.avgTQR || 0) - (b.avgTQR || 0))
				.slice(0, 3)
				.map((ps) => ({
					...ps.player,
					value: ps.avgTQR,
					type: "TQR",
					date: ps.tqrLatestDate,
				}));

			// Top 3 per rating
			const topRated = [...playersWithRating]
				.sort((a, b) => (b.rating || 0) - (a.rating || 0))
				.slice(0, 3)
				.map((ps) => ({
					...ps.player,
					value: ps.rating,
					type: "rating",
					date: null,
				}));

			// Top 3 per RPE (carico più alto)
			const topRPE = [...playersWithRPE]
				.sort((a, b) => (b.avgRPE || 0) - (a.avgRPE || 0))
				.slice(0, 3)
				.map((ps) => ({
					...ps.player,
					value: ps.avgRPE,
					type: "RPE",
					date: ps.rpeLatestDate,
				}));

			// Top 3 con TQR più basso (attenzione)
			const lowTQR = [...playersWithTQR]
				.sort((a, b) => (a.avgTQR || 0) - (b.avgTQR || 0))
				.slice(0, 3)
				.map((ps) => ({
					...ps.player,
					value: ps.avgTQR,
					type: "TQR",
					date: ps.tqrLatestDate,
				}));

			const totalLoadSum = playerStats.reduce(
				(sum, ps) => sum + (ps.rpeLoadSum || 0),
				0,
			);
			const totalLoadCount = playerStats.reduce(
				(sum, ps) => sum + (ps.rpeLoadCount || 0),
				0,
			);
			const avgLoadWeek = totalLoadCount ? totalLoadSum / totalLoadCount : null;

			const totalTqrSum = playerStats.reduce(
				(sum, ps) => sum + (ps.tqrSum || 0),
				0,
			);
			const totalTqrCount = playerStats.reduce(
				(sum, ps) => sum + (ps.tqrCount || 0),
				0,
			);
			const avgTqrWeek = totalTqrCount ? totalTqrSum / totalTqrCount : null;

			return {
				rosterId: roster.id,
				players: playerStats,
				topForm,
				lowForm,
				topRated,
				topRPE,
				lowTQR,
				trainingsCount,
				matchesPlayed,
				avgLoadWeek,
				avgTqrWeek,
			};
		} catch (error) {
			console.error(`Error calculating KPIs for roster ${roster.id}:`, error);
			return {
				rosterId: roster.id,
				players: [],
				topForm: [],
				lowForm: [],
				topRated: [],
				topRPE: [],
				lowTQR: [],
				trainingsCount: 0,
				matchesPlayed: 0,
				avgLoadWeek: null,
				avgTqrWeek: null,
			};
		}
	};

	// Calcola KPI per tutte le rose
	const calculateAllKPIs = async () => {
		setLoading(true);
		try {
			const kpis = {};
			for (const roster of rosters) {
				const kpi = await calculateRosterKPIs(roster);
				kpis[roster.id] = kpi;
			}
			setKpiData(kpis);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getRosters();
	}, [seasonId]);

	useEffect(() => {
		if (rosters.length > 0) {
			calculateAllKPIs();
		}
	}, [rosters]);

	const maxMetrics = useMemo(() => {
		const values = Object.values(kpiData || {});
		const maxLoad = Math.max(
			0,
			...values.map((v) => (v?.avgLoadWeek ? v.avgLoadWeek : 0)),
		);
		const maxTqr = Math.max(
			0,
			...values.map((v) => (v?.avgTqrWeek ? v.avgTqrWeek : 0)),
		);
		return {maxLoad, maxTqr};
	}, [kpiData]);

	const renderMetricBar = (label, value, maxValue, className = "") => {
		const percent =
			value !== null && value !== undefined && maxValue > 0
				? Math.min((value / maxValue) * 100, 100)
				: 0;
		return (
			<div className={`kpi-metric ${className}`}>
				<div className="kpi-metric-header">
					<div className="kpi-metric-label">{label}</div>
					<div className="kpi-metric-value">
						{value !== null && value !== undefined ? value.toFixed(1) : "---"}
					</div>
				</div>
				<div className="kpi-metric-bar">
					<div className="kpi-metric-fill" style={{width: `${percent}%`}} />
				</div>
			</div>
		);
	};

	const renderPlayerList = (players, title, colorClass = "", highlightType) => {
		if (!players || !players.length) {
			return <EmptyData />;
		}

		return (
			<div className="kpi-player-list">
				{players.map((p, index) => (
					<div
						key={p.id || index}
						className={`kpi-player-item ${colorClass} ${
							index === 0 ? "first" : ""
						} ${index === 0 && highlightType ? `first-${highlightType}` : ""}`}
						onClick={() => navigate("/players/" + p.id)}
					>
						<div className="player-rank">#{index + 1}</div>
						<div className="player-info">
							<div className="player-name">
								{p.firstName} {p.lastName}
							</div>
							{p.primaryRole && (
								<div className="player-role">{p.primaryRole}</div>
							)}
						</div>
						<div className="player-metric">
							<div className="player-value">
								{p.value !== null && p.value !== undefined
									? p.type === "rating"
										? p.value.toFixed(2)
										: p.value.toFixed(1)
									: "---"}
							</div>
							{p.date && (
								<div className="player-date">
									{moment(p.date).isValid()
										? moment(p.date).format(dateFormat)
										: "---"}
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		);
	};

	const renderRosterKPIs = (roster) => {
		const kpi = kpiData[roster.id];
		// if (!kpi) {
		// 	return (
		// 		<div className="col-12 padding-small">
		// 			<VLFCard
		// 				title={roster.playerCategory?.name || "Rosa"}
		// 				content={<Loading visible />}
		// 			/>
		// 		</div>
		// 	);
		// }

		return (
			<div className="col-12 padding-small">
				<VLFCard
					title={roster.playerCategory?.name || "Rosa"}
					content={
						<div className="roster-kpi-container">
							<div className="kpi-summary-grid">
								<div className="kpi-summary-card">
									<div className="kpi-summary-label">
										Allenamenti stagione
									</div>
									<div className="kpi-summary-value">
										{kpi?.trainingsCount ?? 0}
									</div>
								</div>
								<div className="kpi-summary-card">
									<div className="kpi-summary-label">Partite giocate</div>
									<div className="kpi-summary-value">
										{kpi?.matchesPlayed ?? 0}
									</div>
								</div>
								<div className="kpi-summary-chart">
									{renderMetricBar(
										"Carico medio (sett.)",
										kpi?.avgLoadWeek,
										maxMetrics.maxLoad,
										"load",
									)}
								</div>
								<div className="kpi-summary-chart">
									{renderMetricBar(
										"TQR medio (sett.)",
										kpi?.avgTqrWeek,
										maxMetrics.maxTqr,
										"tqr",
									)}
								</div>
							</div>
							<div className="row no-margin">
								<div className="col-12 col-md-6 padding-small">
									<div className="kpi-section">
										<div className="kpi-section-title success">
											✓ In Forma (Top 3 TQR)
										</div>
										{renderPlayerList(
											kpi?.topForm,
											"In Forma",
											"success",
											"best",
										)}
									</div>
								</div>
								<div className="col-12 col-md-6 padding-small">
									<div className="kpi-section">
										<div className="kpi-section-title warning">
											⚠ Meno in Forma (Bottom 3 TQR)
										</div>
										{renderPlayerList(
											kpi?.lowForm,
											"Meno in Forma",
											"warning",
											"worst",
										)}
									</div>
								</div>
								<div className="col-12 col-md-6 padding-small">
									<div className="kpi-section">
										<div className="kpi-section-title primary">
											⭐ Top Rating (Top 3)
										</div>
										{renderPlayerList(kpi?.topRated, "Top Rating", "primary")}
									</div>
								</div>
								<div className="col-12 col-md-6 padding-small">
									<div className="kpi-section">
										<div className="kpi-section-title info">
											📊 Alto Carico (Top 3 RPE)
										</div>
										{renderPlayerList(kpi?.topRPE, "Alto Carico", "info")}
									</div>
								</div>
							</div>
						</div>
					}
				/>
			</div>
		);
	};

	const rosterOptions = rosters
		.slice()
		.sort((a, b) => a.playerCategory?.sorting - b.playerCategory?.sorting)
		.map((r) => ({
			value: r.id,
			label: r.playerCategory?.name || "Rosa",
		}));

	const filteredRosters = selectedRoster
		? rosters.filter((r) => r.id === selectedRoster.value)
		: rosters;

	return (
		<Page className="director-dashboard-page">
			<Loading visible={loading} />
			<div className="row no-margin col-container g-2">
				<div className="col-12 padding-small">
					<VLFCard
						title="Filtri"
						openable={false}
						content={
							<div className="row no-margin">
								<div className="col-12 col-md-4">
									<VLFSelectNew
										options={rosterOptions}
										sorted={false}
										value={selectedRoster}
										onChange={setSelectedRoster}
										label="Categoria/Squadra"
										clearable
									/>
								</div>
							</div>
						}
					/>
				</div>
				{filteredRosters
					.sort((a, b) => a.playerCategory?.sorting - b.playerCategory?.sorting)
					.map((roster) => renderRosterKPIs(roster))}
			</div>
		</Page>
	);
}

export default DirectorDashboardPage;
